import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { RichTextParser } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { AddAPet } from '../../components/addAPet/addAPet';
import { PetPolicyDocumentsPanel } from '../../components/documents/petPolicyDocumentsPanel/petPolicyDocumentsPanel';
import { KeyPetDocumentsByPolicyNumber } from '../../helpers/docHelpers';
import { SLICES } from '../../constants/slices';
import { PetDocumentsTable } from '../../components/documents/petDocumentsTable/petDocumentsTable';
import { PetFilter } from '../../components/filters/petFilter';
import { useActivePet } from '../../hooks/activePet';
import './myDocuments.scss';
import { useGetPetInfoQuery } from '../../data/policy';
import { useGetAllDocumentsQuery } from '../../data/document';
import Loading from '../../components/shared/Loading/Loading';
import { TooltipTrigger } from '../../components/tooltipTrigger/tooltipTrigger';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';
import { useGetGeneralTooltipContentQuery } from '../../data/kontentAi';
import { GENERAL_TOOLTIP_CODENAME } from '../../constants/cmsCodeName';

export const MyDocuments = () => {
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { partnerNumber } = authDetails;

  // Using the RTK query hook for getting pet info (only triggered when token is available)
  const { data: petDetails, isLoading: petDetailsLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });

  const { activePolicyId } = useActivePet({ petDetails });

  const { data: petDocuments, isLoading: petDocumentsLoading } = useGetAllDocumentsQuery(
    { partnerNumber, auth0Token: auth0AccessToken },
    { skip: !partnerNumber || !auth0AccessToken }
  );

  const [keyedPetDocuments, setKeyedPetDocuments] = useState();
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;

  const { data: generalTooltip } = useGetGeneralTooltipContentQuery({
    previewEnabled: isKontentAiPreviewEnabled,
    element: [GENERAL_TOOLTIP_CODENAME.CLAIM_DOCUMENTS_TOOLTIP, GENERAL_TOOLTIP_CODENAME.OTHER_DOCUMENTS_TOOLTIP]
  });

  const claimDocumentsTooltip =
    generalTooltip?.content?.[GENERAL_TOOLTIP_CODENAME.CLAIM_DOCUMENTS_TOOLTIP]?.[
      GENERAL_TOOLTIP_CODENAME.CLAIM_DOCUMENTS_TOOLTIP
    ]?.value || '';

  const otherDocumentsTooltip =
    generalTooltip?.content?.[GENERAL_TOOLTIP_CODENAME.OTHER_DOCUMENTS_TOOLTIP]?.[
      GENERAL_TOOLTIP_CODENAME.OTHER_DOCUMENTS_TOOLTIP
    ]?.value || '';

  useEffect(() => {
    if (petDocuments) {
      const data = KeyPetDocumentsByPolicyNumber({ petDocuments });
      setKeyedPetDocuments(data);
    }
  }, [petDocuments]);

  const [policyFilter, setPolicyFilter] = useState(activePolicyId);

  const handlePetFilter = (event) => {
    setPolicyFilter(event.target.value);
  };

  return (
    <main
      className="myDocuments"
      aria-label="My Documents page"
    >
      <h1 className="mainPageHeader">My documents</h1>

      <h2>Policy documents</h2>

      <header className="myDocumentsHeader">
        {petDetailsLoading ? (
          <Loading />
        ) : (
          <PetFilter
            petDetails={petDetails}
            policyFilter={policyFilter}
            onPetFilterChange={handlePetFilter}
          />
        )}
      </header>

      <div className="petDocumentsPanels">
        {petDetailsLoading || petDocumentsLoading ? (
          <Loading />
        ) : (
          petDetails?.map((petDetail) => {
            const { PolicyNumber, FullPolicyNumber, PolicyDetails } = petDetail;

            if (policyFilter && FullPolicyNumber !== policyFilter) {
              return null;
            }

            return (
              <PetPolicyDocumentsPanel
                key={`${PolicyNumber}_policyDocs`}
                petDetail={petDetail}
                policyDetails={PolicyDetails}
                keyedPetDocuments={keyedPetDocuments || []}
                docType="Policies"
                auth0Token={auth0AccessToken}
              />
            );
          })
        )}
      </div>

      <div className="claimDocumentsHeader">
        <h2 className="claimDocumentsHeaderTitle">Claim documents</h2>
        <TooltipTrigger
          id="claim-documents-tooltip"
          trigger={
            <Icon
              icon="information"
              customCssClass="tooltipIcon"
            />
          }
          mode="click"
          content={<>{RichTextParser(claimDocumentsTooltip)}</>}
        />
      </div>
      {petDetailsLoading || petDocumentsLoading ? (
        <Loading />
      ) : (
        <PetDocumentsTable
          petDetails={petDetails}
          documentsData={petDocuments.ClaimDocuments || []}
          tableId="claimsDocs"
          docType="claims"
        />
      )}

      <div className="claimDocumentsHeader">
        <h2 className="claimDocumentsHeaderTitle">Other documents</h2>
        <TooltipTrigger
          id="other-documents-tooltip"
          trigger={
            <Icon
              icon="information"
              customCssClass="tooltipIcon"
            />
          }
          mode="click"
          content={<>{RichTextParser(otherDocumentsTooltip)}</>}
        />
      </div>
      {petDetailsLoading || petDocumentsLoading ? (
        <Loading />
      ) : (
        <PetDocumentsTable
          petDetails={petDetails}
          documentsData={petDocuments.OtherDocuments || []}
          tableId="otherDocs"
          docType="other"
        />
      )}

      <AddAPet />
    </main>
  );
};
